import { 
  collection, 
  getDocs, 
  doc,
  getDoc,
  addDoc,
  updateDoc,
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  AffiliateApplication,
  SocialProfile,
  PaymentInfo,
  CommissionRecord,
  PayoutRecord,
  PayoutSchedule,
  AffiliateDashboardData,
  ClickRecord,
  ConversionRecord,
  ReferralTrackingData
} from '../types/affiliate';
import { getCurrentCommissionRate } from './commissionSettingsService';

// API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

/**
 * Submit an affiliate application
 */
export const submitAffiliateApplication = async (
  userId: string,
  profiles: SocialProfile[],
  website: string | undefined,
  paymentInfo: PaymentInfo
): Promise<string> => {
  try {
    // Get user data to include name and email in application
    let userName = '';
    let userEmail = '';
    
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userName = userData.name || userData.displayName || userData.email?.split('@')[0] || 'Unknown';
        userEmail = userData.email || '';
      }
    } catch (userError) {
      // Continue with application submission even if user data fetch fails
    }

    const applicationData = {
      userId,
      userName,
      userEmail,
      profiles,
      website: website || '',
      paymentInfo,
      status: 'pending' as const,
      submittedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'affiliateApplications'), applicationData);
    return docRef.id;
  } catch (error) {
    throw new Error('Failed to submit affiliate application');
  }
};

/**
 * Get affiliate application by user ID
 */
export const getAffiliateApplication = async (userId: string): Promise<AffiliateApplication | null> => {
  try {
    const q = query(
      collection(db, 'affiliateApplications'),
      where('userId', '==', userId),
      orderBy('submittedAt', 'desc'),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      userId: data.userId,
      profiles: data.profiles || [],
      website: data.website,
      paymentInfo: data.paymentInfo,
      status: data.status,
      referralCode: data.referralCode,
      submittedAt: data.submittedAt?.toDate() || new Date(),
      reviewedBy: data.reviewedBy,
      reviewedAt: data.reviewedAt?.toDate()
    };
  } catch (error) {
    throw new Error('Failed to get affiliate application');
  }
};

/**
 * Get commission records for an affiliate
 */
export const getCommissionRecords = async (
  affiliateId: string,
  startDate?: Date,
  endDate?: Date
): Promise<CommissionRecord[]> => {
  try {
    const q = query(
      collection(db, 'commissionRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    let records = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        orderId: data.orderId,
        orderValue: data.orderValue,
        commissionAmount: data.commissionAmount,
        commissionRate: data.commissionRate || 10,
        status: data.status || 'pending',
        paymentStatus: data.paymentStatus || 'pending',
        source: data.source || 'link', // Default to 'link' for backward compatibility
        discountCodeId: data.discountCodeId || undefined,
        createdAt: data.createdAt?.toDate() || new Date()
      };
    });

    // Client-side filtering by date range if provided
    if (startDate || endDate) {
      records = records.filter(record => {
        if (startDate && record.createdAt < startDate) return false;
        if (endDate && record.createdAt > endDate) return false;
        return true;
      });
    }

    return records;
  } catch (error) {
    throw new Error('Failed to get commission records');
  }
};

/**
 * Get payout records for an affiliate
 */
export const getPayoutRecords = async (affiliateId: string): Promise<PayoutRecord[]> => {
  try {
    const q = query(
      collection(db, 'payoutRecords'),
      where('affiliateId', '==', affiliateId),
      orderBy('paidAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        affiliateId: data.affiliateId,
        amount: data.amount,
        paidAt: data.paidAt?.toDate() || new Date()
      };
    });
  } catch (error) {
    throw new Error('Failed to get payout records');
  }
};

/**
 * Get click and conversion stats for an affiliate
 */
export const getAffiliateStats = async (
  affiliateId: string,
  startDate?: Date,
  endDate?: Date
): Promise<{ clicks: number; conversions: number; totalEarnings: number }> => {
  try {
    // Get click records
    const clicksQuery = query(
      collection(db, 'clickRecords'),
      where('affiliateId', '==', affiliateId)
    );
    const clicksSnapshot = await getDocs(clicksQuery);
    let clicks = clicksSnapshot.docs;

    // Get conversion records
    const conversionsQuery = query(
      collection(db, 'conversionRecords'),
      where('affiliateId', '==', affiliateId)
    );
    const conversionsSnapshot = await getDocs(conversionsQuery);
    let conversions = conversionsSnapshot.docs;

    // Client-side filtering by date range
    if (startDate || endDate) {
      clicks = clicks.filter(doc => {
        const timestamp = doc.data().timestamp?.toDate();
        if (!timestamp) return false;
        if (startDate && timestamp < startDate) return false;
        if (endDate && timestamp > endDate) return false;
        return true;
      });

      conversions = conversions.filter(doc => {
        const timestamp = doc.data().timestamp?.toDate();
        if (!timestamp) return false;
        if (startDate && timestamp < startDate) return false;
        if (endDate && timestamp > endDate) return false;
        return true;
      });
    }

    // Get commission records for earnings calculation
    const commissionRecords = await getCommissionRecords(affiliateId, startDate, endDate);
    const totalEarnings = commissionRecords.reduce((sum, record) => sum + record.commissionAmount, 0);

    return {
      clicks: clicks.length,
      conversions: conversions.length,
      totalEarnings
    };
  } catch (error) {
    throw new Error('Failed to get affiliate stats');
  }
};

/**
 * Get payout schedule configuration
 */
export const getPayoutSchedule = async (): Promise<PayoutSchedule | null> => {
  try {
    const docRef = doc(db, 'payoutSchedule', 'config');
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    const commissionRate = await getCurrentCommissionRate();
    
    return {
      id: docSnap.id,
      frequency: data.frequency,
      dayOfMonth: data.dayOfMonth,
      time: data.time,
      nextPayoutDate: data.nextPayoutDate?.toDate() || new Date(),
      commissionPercentage: commissionRate
    };
  } catch (error) {
    throw new Error('Failed to get payout schedule');
  }
};

/**
 * Get complete affiliate dashboard data
 */
export const getAffiliateDashboardData = async (userId: string): Promise<AffiliateDashboardData | null> => {
  try {
    const application = await getAffiliateApplication(userId);
    if (!application || application.status !== 'approved') {
      return null;
    }

    const [stats, commissionRecords, payoutRecords, payoutSchedule, commissionRate] = await Promise.all([
      getAffiliateStats(application.id),
      getCommissionRecords(application.id),
      getPayoutRecords(application.id),
      getPayoutSchedule(),
      getCurrentCommissionRate()
    ]);

    return {
      application,
      totalClicks: stats.clicks,
      totalConversions: stats.conversions,
      totalEarnings: stats.totalEarnings,
      commissionRecords,
      payoutRecords,
      nextPayoutDate: payoutSchedule?.nextPayoutDate || null,
      commissionPercentage: commissionRate
    };
  } catch (error) {
    throw new Error('Failed to get affiliate dashboard data');
  }
};

/**
 * Referral tracking functions
 */

// Set referral cookie
export const setReferralCookie = (referralCode: string, expiryDays: number = 30): void => {
  try {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryDays);
    
    const cookieData: ReferralTrackingData = {
      referralCode,
      affiliateId: '', // Will be populated when validating
      expiryDate
    };

    document.cookie = `jampackd_ref=${JSON.stringify(cookieData)}; expires=${expiryDate.toUTCString()}; path=/`;
  } catch (error) {
    // Silent error handling
  }
};

// Get referral cookie
export const getReferralCookie = (): ReferralTrackingData | null => {
  try {
    const cookies = document.cookie.split(';');
    const refCookie = cookies.find(cookie => cookie.trim().startsWith('jampackd_ref='));
    
    if (!refCookie) {
      return null;
    }

    const cookieValue = refCookie.split('=')[1];
    const data = JSON.parse(decodeURIComponent(cookieValue));
    
    // Check if cookie has expired
    const expiryDate = new Date(data.expiryDate);
    if (expiryDate < new Date()) {
      clearReferralCookie();
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Clear referral cookie
export const clearReferralCookie = (): void => {
  document.cookie = 'jampackd_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Validate referral code and record click
 */
export const validateReferralCode = async (referralCode: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/affiliate/validate-referral`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ referralCode })
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.valid;
  } catch (error) {
    return false;
  }
};

/**
 * Record conversion when order is completed
 */
export const recordConversion = async (orderId: string, orderValue: number): Promise<void> => {
  try {
    const referralData = getReferralCookie();
    if (!referralData) {
      return;
    }

    await fetch(`${API_BASE_URL}/affiliate/record-conversion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        referralCode: referralData.referralCode,
        orderId,
        orderValue
      })
    });

    // Keep the referral cookie for the full 30-day period to allow
    // affiliates to earn from repeat purchases within that window
    // The cookie will automatically expire after 30 days
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Get commission source analytics for affiliate dashboard
 */
export const getCommissionSourceAnalytics = async (affiliateId: string, startDate?: Date, endDate?: Date) => {
  try {
    const commissionsRef = collection(db, 'commissionRecords');
    
    // Add date filters if provided
    const constraints = [where('affiliateId', '==', affiliateId)];
    if (startDate) constraints.push(where('createdAt', '>=', startDate));
    if (endDate) constraints.push(where('createdAt', '<=', endDate));
    
    const commissionsQuery = query(commissionsRef, ...constraints);
    const snapshot = await getDocs(commissionsQuery);
    
    let linkStats = { count: 0, totalEarnings: 0, totalOrderValue: 0 };
    let discountStats = { count: 0, totalEarnings: 0, totalOrderValue: 0 };
    
    snapshot.docs.forEach(doc => {
      const data = doc.data() as any;
      const source = data.source || 'link'; // Default to link for older records
      const earnings = data.commissionAmount || 0;
      const orderValue = data.orderValue || 0;
      
      if (source === 'discount_code') {
        discountStats.count++;
        discountStats.totalEarnings += earnings;
        discountStats.totalOrderValue += orderValue;
      } else {
        linkStats.count++;
        linkStats.totalEarnings += earnings;
        linkStats.totalOrderValue += orderValue;
      }
    });
    
    const totalCommissions = linkStats.count + discountStats.count;
    const totalEarnings = linkStats.totalEarnings + discountStats.totalEarnings;
    
    return {
      linkStats: {
        ...linkStats,
        avgOrderValue: linkStats.count > 0 ? linkStats.totalOrderValue / linkStats.count : 0,
        avgCommission: linkStats.count > 0 ? linkStats.totalEarnings / linkStats.count : 0
      },
      discountStats: {
        ...discountStats,
        avgOrderValue: discountStats.count > 0 ? discountStats.totalOrderValue / discountStats.count : 0,
        avgCommission: discountStats.count > 0 ? discountStats.totalEarnings / discountStats.count : 0
      },
      summary: {
        totalCommissions,
        totalEarnings,
        linkPercentage: totalCommissions > 0 ? (linkStats.count / totalCommissions) * 100 : 0,
        discountPercentage: totalCommissions > 0 ? (discountStats.count / totalCommissions) * 100 : 0,
        linkEarningsPercentage: totalEarnings > 0 ? (linkStats.totalEarnings / totalEarnings) * 100 : 0,
        discountEarningsPercentage: totalEarnings > 0 ? (discountStats.totalEarnings / totalEarnings) * 100 : 0
      }
    };
  } catch (error) {
    throw new Error('Failed to get commission source analytics');
  }
};

/**
 * Get discount codes linked to affiliate
 */
export const getAffiliateDiscountCodes = async (affiliateId: string) => {
  try {
    const discountRef = doc(db, 'config', 'discounts');
    const discountDoc = await getDoc(discountRef);
    
    if (!discountDoc.exists()) {
      return [];
    }
    
    const data = discountDoc.data();
    if (!data.discountCodes || !Array.isArray(data.discountCodes)) {
      return [];
    }
    
    // Filter discount codes for this affiliate
    return data.discountCodes
      .filter((discount: any) => discount.affiliateId === affiliateId)
      .map((discount: any) => ({
        id: discount.id,
        code: discount.code,
        description: discount.description,
        type: discount.type,
        value: discount.value,
        minOrderAmount: discount.minOrderAmount,
        maxUses: discount.maxUses,
        currentUses: discount.currentUses,
        status: discount.status,
        startDate: discount.startDate,
        endDate: discount.endDate,
        applicableProducts: discount.applicableProducts,
        affiliateId: discount.affiliateId,
        createdAt: discount.createdAt,
        updatedAt: discount.updatedAt
      }));
  } catch (error) {
    return [];
  }
};

/**
 * Get discount code performance for affiliate
 */
export const getDiscountCodePerformance = async (affiliateId: string, startDate?: Date, endDate?: Date) => {
  try {
    const commissionsRef = collection(db, 'commissionRecords');
    let constraints = [
      where('affiliateId', '==', affiliateId),
      where('source', '==', 'discount_code')
    ];
    
    if (startDate) constraints.push(where('createdAt', '>=', startDate));
    if (endDate) constraints.push(where('createdAt', '<=', endDate));
    
    const commissionsQuery = query(commissionsRef, ...constraints);
    const snapshot = await getDocs(commissionsQuery);
    
    const codePerformance: { [key: string]: any } = {};
    
    snapshot.docs.forEach(doc => {
      const data = doc.data() as any;
      const codeId = data.discountCodeId;
      if (!codeId) return;
      
      if (!codePerformance[codeId]) {
        codePerformance[codeId] = {
          discountCodeId: codeId,
          totalCommissions: 0,
          totalEarnings: 0,
          totalOrderValue: 0,
          avgOrderValue: 0
        };
      }
      
      codePerformance[codeId].totalCommissions++;
      codePerformance[codeId].totalEarnings += data.commissionAmount || 0;
      codePerformance[codeId].totalOrderValue += data.orderValue || 0;
    });
    
    // Calculate averages and sort by earnings
    const performanceArray = Object.values(codePerformance).map((perf: any) => ({
      ...perf,
      avgOrderValue: perf.totalCommissions > 0 ? perf.totalOrderValue / perf.totalCommissions : 0
    })).sort((a: any, b: any) => b.totalEarnings - a.totalEarnings);
    
    return performanceArray.slice(0, 10); // Top 10 performing codes
  } catch (error) {
    return [];
  }
};